<script setup>
/* eslint-disable */
import { computed } from 'vue'
import { GaiaCalendar } from '@/utils/gaiaCalendar'
import { MonthlyRaceManager } from '@/services/monthlyRaceManager'
import { useGameStore } from '@/store/game'

const props = defineProps({
  bestTimes: {
    type: Object,
    required: true
  },
  prevMonth: {
    type: Number,
    required: true
  },
  year: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['close'])

const gameStore = useGameStore()

const isPlayerChocobo = (chocobo) => {
  if (!chocobo || !gameStore.playerOwner) return false
  return gameStore.playerOwner.chocoboIds.includes(chocobo.id)
}

const playerWinCount = computed(() => {
  let count = 0
  if (isPlayerChocobo(props.bestTimes.short?.chocobo)) count++
  if (isPlayerChocobo(props.bestTimes.medium?.chocobo)) count++
  if (isPlayerChocobo(props.bestTimes.long?.chocobo)) count++
  return count
})

const formatTime = (timeData) => {
  // Check if we have valid time data
  if (!timeData || !timeData.raw || timeData.raw === Infinity) {
    return 'No valid times'
  }
  
  const timeValue = typeof timeData.raw === 'string' ? parseFloat(timeData.raw) : timeData.raw
  
  // Safety check for NaN
  if (isNaN(timeValue)) {
    return 'No valid times'
  }
  
  const minutes = Math.floor(timeValue / 60)
  const seconds = Math.floor(timeValue % 60)
  const milliseconds = Math.floor((timeValue % 1) * 1000)
  
  // Format with padding
  const formattedMinutes = minutes.toString()
  const formattedSeconds = seconds.toString().padStart(2, '0')
  const formattedMilliseconds = milliseconds.toString().padStart(3, '0')
  
  return `${formattedMinutes}:${formattedSeconds}.${formattedMilliseconds}`
}


</script>

<template>
  <!-- Template remains mostly the same, just update the time formatting calls -->
  <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div class="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
      <div class="p-6 overflow-y-auto flex-1">
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-2xl font-bold">
            Monthly Race Results
          </h2>
          <div class="text-lg text-gray-600">
            Month {{ prevMonth }}/εγλ {{ year }}
          </div>
        </div>

        <!-- Player Wins Summary -->
        <div v-if="playerWinCount > 0" class="mb-6 p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
          <div class="flex items-center gap-3">
            <span class="text-3xl">🎉</span>
            <div>
              <h3 class="text-lg font-bold text-yellow-800">Congratulations!</h3>
              <p class="text-yellow-700">
                Your chocobos won {{ playerWinCount }} {{ playerWinCount === 1 ? 'category' : 'categories' }} this month!
                Check your inventory for prizes.
              </p>
            </div>
          </div>
        </div>

        <div class="space-y-6">
          <!-- Sprint Results -->
          <div 
            :class="[
              'rounded-lg p-4',
              isPlayerChocobo(bestTimes.short?.chocobo) 
                ? 'bg-yellow-100 border-2 border-yellow-400' 
                : 'bg-blue-50'
            ]"
          >
            <h3 class="text-lg font-semibold mb-3">800m Sprint Championship</h3>
            <div class="space-y-2">
              <div class="flex items-center gap-4">
                <div class="w-8 h-8 flex items-center justify-center bg-yellow-100 text-yellow-800 rounded-full font-bold">
                  1
                </div>
                <div class="flex-1">
                  <div class="flex items-center gap-2">
                    <div class="font-medium">{{ bestTimes.short.chocobo?.name || 'No record set' }}</div>
                    <span 
                      v-if="isPlayerChocobo(bestTimes.short?.chocobo)"
                      class="text-xs bg-yellow-500 text-white px-2 py-1 rounded-full font-medium"
                    >
                      YOUR CHOCOBO - PRIZE WINNER!
                    </span>
                  </div>
                  <div class="text-sm text-gray-600">{{ formatTime(bestTimes.short) }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Medium Race Results -->
          <div 
            :class="[
              'rounded-lg p-4',
              isPlayerChocobo(bestTimes.medium?.chocobo) 
                ? 'bg-yellow-100 border-2 border-yellow-400' 
                : 'bg-green-50'
            ]"
          >
            <h3 class="text-lg font-semibold mb-3">2000m Race Championship</h3>
            <div class="space-y-2">
              <div class="flex items-center gap-4">
                <div class="w-8 h-8 flex items-center justify-center bg-yellow-100 text-yellow-800 rounded-full font-bold">
                  1
                </div>
                <div class="flex-1">
                  <div class="flex items-center gap-2">
                    <div class="font-medium">{{ bestTimes.medium.chocobo?.name || 'No record set' }}</div>
                    <span 
                      v-if="isPlayerChocobo(bestTimes.medium?.chocobo)"
                      class="text-xs bg-yellow-500 text-white px-2 py-1 rounded-full font-medium"
                    >
                      YOUR CHOCOBO - PRIZE WINNER!
                    </span>
                  </div>
                  <div class="text-sm text-gray-600">{{ formatTime(bestTimes.medium) }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Endurance Results -->
          <div 
            :class="[
              'rounded-lg p-4',
              isPlayerChocobo(bestTimes.long?.chocobo) 
                ? 'bg-yellow-100 border-2 border-yellow-400' 
                : 'bg-purple-50'
            ]"
          >
            <h3 class="text-lg font-semibold mb-3">5000m Endurance Championship</h3>
            <div class="space-y-2">
              <div class="flex items-center gap-4">
                <div class="w-8 h-8 flex items-center justify-center bg-yellow-100 text-yellow-800 rounded-full font-bold">
                  1
                </div>
                <div class="flex-1">
                  <div class="flex items-center gap-2">
                    <div class="font-medium">{{ bestTimes.long.chocobo?.name || 'No record set' }}</div>
                    <span 
                      v-if="isPlayerChocobo(bestTimes.long?.chocobo)"
                      class="text-xs bg-yellow-500 text-white px-2 py-1 rounded-full font-medium"
                    >
                      YOUR CHOCOBO - PRIZE WINNER!
                    </span>
                  </div>
                  <div class="text-sm text-gray-600">{{ formatTime(bestTimes.long) }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Trophy Awards Section -->
          <div v-if="bestTimes.awards?.length" class="mt-6 p-4 bg-yellow-50 rounded-lg">
            <h3 class="text-lg font-semibold mb-3">🏆 Your Ranch's Achievements</h3>
            <div class="space-y-2">
              <div 
                v-for="award in bestTimes.awards"
                :key="award.name"
                class="flex items-center gap-2"
              >
                <span class="text-xl">🏆</span>
                <div>
                  <div class="font-medium">{{ award.name }}</div>
                  <div class="text-sm text-gray-600">
                    Awarded to {{ award.chocobo }}
                  </div>
                  <div class="text-sm text-green-600 font-medium mt-1">
                    Prize Money: {{ award.prizeDisplay }}
                  </div>
                </div>
              </div>
            </div>
            <div class="mt-4 pt-4 border-t border-yellow-200">
              <div class="text-green-600 font-medium">
                Total Prize Money: {{ bestTimes.awards.reduce((total, award) => total + award.value, 0).toLocaleString() }}G
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-end mt-6">
          <button
            @click="$emit('close')"
            class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>